import aiFilterApi from "@/api/ai-filter";
import storageApi from "@/api/storage";
import ClipArtInputField from "@/components/common/ClipArtInputField";
import { ClipArt } from "@/interfaces/clip-art.interface";
import InputField from "@/components/common/InputField";
import SelectField from "@/components/common/SelectField";
import PostProcessingFields from "./PostProcessingFields";
import { AIFilter, AIFilterProvider } from "@/interfaces/ai-filter.interface";
import { uploadAIFilterJson } from "@/utils/ai-filter";
import { Info, Trash2, TriangleAlert } from "lucide-react";
import { Button } from "primereact/button";
import { confirmPopup } from "primereact/confirmpopup";
import { Dialog } from "primereact/dialog";
import { Fieldset } from "primereact/fieldset";
import { InputTextarea } from "primereact/inputtextarea";
import { MouseEvent, useEffect, useMemo, useState } from "react";
import {
  ASPECT_RATIO_AUTO_VALUE,
  AIFilterType,
  getDefaultBuiltInProcessingConfig,
  getDefaultCutoutConfig,
  getDefaultGoogleConfig,
  getDefaultOpenAIConfig,
  getDefaultTypeByProvider,
  getDefaultVertexConfig,
  getProviderLabel,
  getTypeFieldOptionsByProvider,
  getTypeLabelByProvider,
  normalizeTypeByProvider,
  GOOGLE_ASPECT_RATIO_FIELD_OPTIONS,
  GOOGLE_IMAGE_SIZE_FIELD_OPTIONS,
  GOOGLE_MODEL_FIELD_OPTIONS,
  IMAGE_SIZE_AUTO_VALUE,
  OPENAI_SIZE_FIELD_OPTIONS,
  OPENAI_SIZE_AUTO_VALUE,
  BuiltInProcessingConfigForm,
  buildConfigByProvider,
  CUTOUT_CROP_FIELD_OPTIONS,
  CUTOUT_OUTPUT_FORMAT_FIELD_OPTIONS,
  getCutoutOutputFormatLabel,
  fetchGoogleImageModelFieldOptions,
  fetchOpenAIImageModelFieldOptions,
  GoogleConfigForm,
  OpenAIConfigForm,
  CutoutConfigForm,
  OPENAI_MODEL_FIELD_OPTIONS,
  PROVIDER_FIELD_OPTIONS,
  supportsGoogleImageSizeConfig,
  getPostProcessingTypeLabel,
  VertexConfigForm,
} from "./form-config";

interface Props {
  data: AIFilter;
}

interface FilterOption {
  key: string;
  label: string;
  value: string;
}

const AIFilterName = ({ data }: Props) => {
  return (
    <span
      className="block px-2 text-gray-700 truncate line-clamp-1 text-md"
      title={String(data.name)}
    >
      {data.name}
    </span>
  );
};

const AIFilterProviderCell = ({ data }: Props) => {
  const providerLabel = getProviderLabel(data.provider);

  return (
    <span
      className="block px-2 text-gray-700 truncate line-clamp-1 text-md"
      title={providerLabel}
    >
      {providerLabel}
    </span>
  );
};

const AIFilterTypeCell = ({ data }: Props) => {
  const typeLabel = getTypeLabelByProvider(data.provider, data.type);

  return (
    <span
      className="block px-2 text-gray-700 truncate line-clamp-1 text-md"
      title={typeLabel}
    >
      {typeLabel}
    </span>
  );
};

const AIFilterConfigRows = ({
  rows,
}: {
  rows: Array<{ label: string; value: string }>;
}) => (
  <div className="flex flex-col gap-0.5 py-1 text-md text-gray-600">
    {rows.map((row) => (
      <div
        key={row.label}
        className="line-clamp-1"
        title={`${row.label}: ${row.value}`}
      >
        <span className="font-medium text-gray-700">{row.label}:</span>{" "}
        {row.value}
      </div>
    ))}
  </div>
);

const getPostProcessingConfigRows = (config: any): any[] => {
  const rows = [
    {
      label: "Post Processing Type",
      value: getPostProcessingTypeLabel(config.postProcessingType),
    },
  ];

  if (
    config.postProcessingType === "color_removal_and_fill" &&
    config.postProcessingConfig
  ) {
    rows.push({
      label: "Remove Color",
      value: config.postProcessingConfig.removeColor,
    });
    rows.push({
      label: "Fill Color",
      value: config.postProcessingConfig.fillColor ?? "-",
    });
  }

  return rows;
};

const AIFilterConfig = ({ data }: Props) => {
  if (data.provider === "google") {
    const config = getDefaultGoogleConfig(data.config);
    const rows = [
      { label: "Model", value: config.model || "-" },
      { label: "Prompt", value: config.prompt || "-" },
      { label: "Aspect Ratio", value: config.aspectRatio ?? "Auto" },
    ];
    if (supportsGoogleImageSizeConfig(config.model)) {
      rows.push({ label: "Image Size", value: config.imageSize ?? "Auto" });
    }
    rows.push(...getPostProcessingConfigRows(config));
    return <AIFilterConfigRows rows={rows} />;
  }

  if (data.provider === "openai") {
    const config = getDefaultOpenAIConfig(data.config);
    const rows = [
      { label: "Model", value: config.model || "-" },
      { label: "Prompt", value: config.prompt || "-" },
      { label: "Size", value: config.size ?? "Auto" },
      ...getPostProcessingConfigRows(config),
    ];
    return <AIFilterConfigRows rows={rows} />;
  }

  if (data.provider === "cutout.pro") {
    const config = getDefaultCutoutConfig(data.config);
    const rows = [
      { label: "Crop", value: config.crop ? "Enabled" : "Disabled" },
      {
        label: "Output Format",
        value: getCutoutOutputFormatLabel(config.outputFormat),
      },
      ...getPostProcessingConfigRows(config),
    ];
    return <AIFilterConfigRows rows={rows} />;
  }

  if (data.provider === "built_in_processing") {
    const config = getDefaultBuiltInProcessingConfig(data.config);
    const rows = [
      { label: "Model", value: config.model || "-" },
      { label: "Aspect Ratio", value: config.aspectRatio ?? "Auto" },
    ];
    if (supportsGoogleImageSizeConfig(config.model)) {
      rows.push({ label: "Image Size", value: config.imageSize ?? "Auto" });
    }
    rows.push(...getPostProcessingConfigRows(config));
    return <AIFilterConfigRows rows={rows} />;
  }

  if (data.provider === "vertex") {
    const config = getDefaultVertexConfig(data.config);
    const rows = [
      { label: "Model", value: config.model || "-" },
      { label: "Prompt", value: config.prompt || "-" },
      { label: "Aspect Ratio", value: config.aspectRatio ?? "Auto" },
    ];
    if (supportsGoogleImageSizeConfig(config.model)) {
      rows.push({ label: "Image Size", value: config.imageSize ?? "Auto" });
    }
    rows.push(...getPostProcessingConfigRows(config));
    return <AIFilterConfigRows rows={rows} />;
  }

  return (
    <div className="flex flex-col gap-0.5 py-1 text-md text-gray-600">
      <div className="line-clamp-1" title="No configuration required">
        <span className="font-medium text-gray-700">Config:</span> No
        configuration
      </div>
    </div>
  );
};

const AIFilterActions = ({
  data,
  onDelete,
  onDetails,
}: Props & {
  onDelete?: (item: AIFilter) => void;
  onDetails?: (item: AIFilter) => void;
}) => {
  const onConfirmDelete = (event: MouseEvent<HTMLElement>) => {
    confirmPopup({
      target: event.currentTarget,
      message: "This action cannot be undone. Confirm?",
      icon: <TriangleAlert color="red" />,
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      accept: () => onDelete?.(data),
      reject: () => {},
    });
  };

  return (
    <div className="flex gap-2">
      <Button
        severity="info"
        outlined
        style={{
          width: 28,
          height: 28,
        }}
        tooltip="Details"
        tooltipOptions={{ position: "top" }}
        icon={<Info size={18} />}
        onClick={() => onDetails?.(data)}
      />
      <Button
        severity="danger"
        outlined
        style={{
          width: 28,
          height: 28,
        }}
        tooltip="Delete"
        tooltipOptions={{ position: "top" }}
        icon={<Trash2 size={18} />}
        onClick={onConfirmDelete}
      />
    </div>
  );
};

const AIFilterDetails = ({
  data,
  onClose,
  onUpdated,
}: {
  data?: AIFilter;
  onClose?: () => void;
  onUpdated?: (item: AIFilter) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [provider, setProvider] = useState<AIFilterProvider>("google");
  const [type, setType] = useState<AIFilterType>(
    getDefaultTypeByProvider("google"),
  );
  const [googleConfig, setGoogleConfig] = useState<GoogleConfigForm>(
    getDefaultGoogleConfig(),
  );
  const [openAIConfig, setOpenAIConfig] = useState<OpenAIConfigForm>(
    getDefaultOpenAIConfig(),
  );
  const [cutoutConfig, setCutoutConfig] = useState<CutoutConfigForm>(
    getDefaultCutoutConfig(),
  );
  const [builtInProcessingConfig, setBuiltInProcessingConfig] =
    useState<BuiltInProcessingConfigForm>(getDefaultBuiltInProcessingConfig());
  const [vertexConfig, setVertexConfig] = useState<VertexConfigForm>(
    getDefaultVertexConfig(),
  );
  const [googleModelFieldOptions, setGoogleModelFieldOptions] = useState(
    GOOGLE_MODEL_FIELD_OPTIONS,
  );
  const [openAIModelFieldOptions, setOpenAIModelFieldOptions] = useState(
    OPENAI_MODEL_FIELD_OPTIONS,
  );
  const [fallbackFilterId, setFallbackFilterId] = useState("undefined");
  const [fallbackFilterOptions, setFallbackFilterOptions] = useState<
    FilterOption[]
  >([]);
  const [fallbackFilterLoading, setFallbackFilterLoading] = useState(false);
  const [referenceImageUrls, setReferenceImageUrls] = useState<string[]>([]);
  const [newRefSlotKey, setNewRefSlotKey] = useState(0);

  const typeFieldOptions = useMemo(
    () => getTypeFieldOptionsByProvider(provider),
    [provider],
  );
  const showImageSizeField = useMemo(
    () =>
      provider === "google" &&
      supportsGoogleImageSizeConfig(googleConfig.model),
    [provider, googleConfig.model],
  );
  const showBuiltInImageSizeField = useMemo(
    () =>
      provider === "built_in_processing" &&
      supportsGoogleImageSizeConfig(builtInProcessingConfig.model),
    [provider, builtInProcessingConfig.model],
  );
  const showVertexImageSizeField = useMemo(
    () =>
      provider === "vertex" && supportsGoogleImageSizeConfig(vertexConfig.model),
    [provider, vertexConfig.model],
  );
  const showFallbackConfiguration = useMemo(
    () =>
      provider === "google" ||
      provider === "openai" ||
      provider === "built_in_processing" ||
      provider === "vertex",
    [provider],
  );
  const showReferenceImageField = useMemo(
    () => provider === "google" || provider === "openai" || provider === "vertex",
    [provider],
  );
  const normalizedName = useMemo(() => name.trim(), [name]);
  const normalizedFallbackFilterId = useMemo(
    () =>
      !fallbackFilterId || fallbackFilterId === "undefined"
        ? undefined
        : fallbackFilterId,
    [fallbackFilterId],
  );
  const normalizedConfig = useMemo(
    () =>
      buildConfigByProvider(provider, {
        googleConfig,
        openAIConfig,
        cutoutConfig,
        builtInProcessingConfig,
        vertexConfig,
      }),
    [
      provider,
      googleConfig,
      openAIConfig,
      cutoutConfig,
      builtInProcessingConfig,
      vertexConfig,
    ],
  );
  const providerModelFieldOptions = useMemo(() => {
    if (provider === "openai") {
      return openAIModelFieldOptions;
    }
    return googleModelFieldOptions;
  }, [provider, googleModelFieldOptions, openAIModelFieldOptions]);

  const postProcessingValues = {
    google: googleConfig,
    openai: openAIConfig,
    "cutout.pro": cutoutConfig,
    built_in_processing: builtInProcessingConfig,
    vertex: vertexConfig,
  }[provider];

  const onChangePostProcessing = (update: any) => {
    if (provider === "openai") {
      setOpenAIConfig((prev) => ({ ...prev, ...update }));
      return;
    }
    if (provider === "cutout.pro") {
      setCutoutConfig((prev) => ({ ...prev, ...update }));
      return;
    }
    if (provider === "built_in_processing") {
      setBuiltInProcessingConfig((prev) => ({ ...prev, ...update }));
      return;
    }
    if (provider === "vertex") {
      setVertexConfig((prev) => ({ ...prev, ...update }));
      return;
    }
    setGoogleConfig((prev) => ({ ...prev, ...update }));
  };

  useEffect(() => {
    if (provider !== "google") {
      return;
    }

    const allowedValues = new Set(
      googleModelFieldOptions.map((option) => option.value),
    );
    const currentModel = googleConfig.model.trim();
    const nextModel = googleModelFieldOptions[0]?.value || "";

    if (currentModel && allowedValues.has(currentModel)) {
      return;
    }

    if (currentModel === nextModel) {
      return;
    }

    setGoogleConfig((prev) => ({
      ...prev,
      model: nextModel,
    }));
  }, [provider, googleModelFieldOptions, googleConfig.model]);

  useEffect(() => {
    if (provider !== "openai") {
      return;
    }

    const allowedValues = new Set(
      openAIModelFieldOptions.map((option) => option.value),
    );
    const currentModel = openAIConfig.model.trim();
    const nextModel = openAIModelFieldOptions[0]?.value || "";

    if (currentModel && allowedValues.has(currentModel)) {
      return;
    }

    if (currentModel === nextModel) {
      return;
    }

    setOpenAIConfig((prev) => ({
      ...prev,
      model: nextModel,
    }));
  }, [provider, openAIModelFieldOptions, openAIConfig.model]);

  useEffect(() => {
    if (provider !== "vertex") {
      return;
    }

    const allowedValues = new Set(
      googleModelFieldOptions.map((option) => option.value),
    );
    const currentModel = vertexConfig.model.trim();
    const nextModel = googleModelFieldOptions[0]?.value || "";

    if (currentModel && allowedValues.has(currentModel)) {
      return;
    }

    if (currentModel === nextModel) {
      return;
    }

    setVertexConfig((prev) => ({
      ...prev,
      model: nextModel,
    }));
  }, [provider, googleModelFieldOptions, vertexConfig.model]);
  const initialValues = useMemo(() => {
    if (!data) {
      return null;
    }

    const initialProvider = data.provider || "google";
    return {
      fallbackId: data.fallbackId,
      name: (data.name || "").trim(),
      provider: initialProvider,
      type: normalizeTypeByProvider(initialProvider, data.type),
      referenceImageUrls: data.referenceImageUrls ?? [],
      config: buildConfigByProvider(initialProvider, {
        googleConfig: getDefaultGoogleConfig(data.config),
        openAIConfig: getDefaultOpenAIConfig(data.config),
        cutoutConfig: getDefaultCutoutConfig(data.config),
        builtInProcessingConfig: getDefaultBuiltInProcessingConfig(data.config),
        vertexConfig: getDefaultVertexConfig(data.config),
      }),
    };
  }, [data]);
  const hasChanges = useMemo(() => {
    if (!initialValues) {
      return false;
    }
    if (
      normalizedFallbackFilterId !== initialValues.fallbackId ||
      normalizedName !== initialValues.name ||
      provider !== initialValues.provider ||
      type !== initialValues.type ||
      JSON.stringify(referenceImageUrls) !== JSON.stringify(initialValues.referenceImageUrls)
    ) {
      return true;
    }
    return (
      JSON.stringify(normalizedConfig) !== JSON.stringify(initialValues.config)
    );
  }, [
    initialValues,
    normalizedFallbackFilterId,
    normalizedName,
    provider,
    type,
    normalizedConfig,
    referenceImageUrls,
  ]);
  const canSave = useMemo(() => {
    const hasName = Boolean(normalizedName);
    const hasGoogleModel =
      provider !== "google" || Boolean(googleConfig.model.trim());
    const hasOpenAIModel =
      provider !== "openai" || Boolean(openAIConfig.model.trim());
    const hasBuiltInModel =
      provider !== "built_in_processing" ||
      Boolean(builtInProcessingConfig.model.trim());
    const hasVertexModel =
      provider !== "vertex" || Boolean(vertexConfig.model.trim());
    return (
      hasName &&
      hasGoogleModel &&
      hasOpenAIModel &&
      hasBuiltInModel &&
      hasVertexModel &&
      hasChanges
    );
  }, [
    normalizedName,
    provider,
    googleConfig.model,
    openAIConfig.model,
    builtInProcessingConfig.model,
    vertexConfig.model,
    hasChanges,
  ]);

  useEffect(() => {
    if (data) {
      setOpen(true);
      setName(data.name || "");
      setProvider(data.provider || "google");
      setType(normalizeTypeByProvider(data.provider, data.type));
      setGoogleConfig(getDefaultGoogleConfig(data.config));
      setOpenAIConfig(getDefaultOpenAIConfig(data.config));
      setCutoutConfig(getDefaultCutoutConfig(data.config));
      setFallbackFilterId(data.fallbackId || "undefined");
      setBuiltInProcessingConfig(
        getDefaultBuiltInProcessingConfig(data.config),
      );
      setVertexConfig(getDefaultVertexConfig(data.config));
      setReferenceImageUrls(data.referenceImageUrls ?? []);
    } else {
      setOpen(false);
    }
  }, [data]);

  useEffect(() => {
    let cancelled = false;

    const loadModelOptions = async () => {
      const [googleOptions, openAIOptions] = await Promise.all([
        fetchGoogleImageModelFieldOptions(),
        fetchOpenAIImageModelFieldOptions(),
      ]);
      if (!cancelled) {
        setGoogleModelFieldOptions(googleOptions);
        setOpenAIModelFieldOptions(openAIOptions);
      }
    };

    loadModelOptions();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    const loadFallbackFilters = async () => {
      setFallbackFilterLoading(true);
      try {
        const response = await aiFilterApi.getMany({
          page: 1,
          size: 1000,
          sort: "name",
        });

        if (cancelled) return;

        setFallbackFilterOptions(
          response.data.map((filter) => ({
            key: filter._id,
            value: filter._id,
            label: filter.name + ` (${filter.provider})`,
          })),
        );
      } catch {
        if (cancelled) return;
        setFallbackFilterOptions([]);
      } finally {
        if (!cancelled) {
          setFallbackFilterLoading(false);
        }
      }
    };

    loadFallbackFilters();

    return () => {
      cancelled = true;
    };
  }, [open]);

  const availableFallbackFilterOptions = fallbackFilterOptions;

  const onHide = () => {
    setOpen(false);
    onClose?.();
  };

  const onChangeProvider = (nextProvider: AIFilterProvider) => {
    setProvider(nextProvider);
    setType(normalizeTypeByProvider(nextProvider, type));
  };

  const onSave = async () => {
    if (!data) return;
    if (!canSave) return;

    setSaving(true);
    try {
      const response = await aiFilterApi.updateOneById(data._id, {
        fallbackId: normalizedFallbackFilterId,
        name: normalizedName,
        provider,
        type,
        config: normalizedConfig,
        referenceImageUrls: showReferenceImageField ? referenceImageUrls : [],
      });

      const updatedItem: AIFilter = {
        ...data,
        ...response.data,
        referenceImageUrls: showReferenceImageField && referenceImageUrls.length > 0 ? referenceImageUrls : undefined,
      };
      try {
        await uploadAIFilterJson(updatedItem);
      } catch {}

      onUpdated?.(updatedItem);
      onHide();
    } catch {}
    setSaving(false);
  };

  return (
    <Dialog
      visible={open}
      header="AI filter details"
      footer={
        <div className="flex justify-end gap-2">
          <Button
            outlined
            label="Cancel"
            onClick={onHide}
            disabled={saving}
            style={{ height: 36 }}
          />
          <Button
            label="Save"
            onClick={onSave}
            loading={saving}
            disabled={saving || !canSave}
            style={{ height: 36 }}
          />
        </div>
      }
      onHide={onHide}
    >
      <div className="flex w-[640px] max-w-full flex-col gap-4">
        <Fieldset className="ai-filter-fieldset" legend="General">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <InputField
                className="flex-col items-start w-full gap-1"
                inputClassName="h-9"
                label="Name"
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </div>
            <SelectField
              className="flex-col items-start w-full gap-1"
              inputClassName="h-9 px-2"
              label="Provider"
              value={provider}
              options={PROVIDER_FIELD_OPTIONS}
              onChange={(event) =>
                onChangeProvider(event.target.value as AIFilterProvider)
              }
            />
            <SelectField
              className="flex-col items-start w-full gap-1"
              inputClassName="h-9 px-2"
              label="Type"
              value={type}
              options={typeFieldOptions}
              onChange={(event) => setType(event.target.value as AIFilterType)}
            />
          </div>
        </Fieldset>

        {showFallbackConfiguration && (
          <Fieldset
            className="ai-filter-fieldset"
            legend="Fallback Configuration (Optional)"
          >
            <div className="grid grid-cols-1 gap-3">
              <SelectField
                className="flex-col items-start w-full gap-1"
                inputClassName="h-9 px-2"
                label="AI filters"
                value={fallbackFilterId}
                options={availableFallbackFilterOptions}
                disabled={fallbackFilterLoading}
                onChange={(event) => setFallbackFilterId(event.target.value)}
              />
              {fallbackFilterLoading && (
                <div className="text-sm text-gray-500">
                  Loading fallback AI filters...
                </div>
              )}
            </div>
          </Fieldset>
        )}

        <Fieldset className="ai-filter-fieldset" legend="Configuration">
          {provider === "google" || provider === "openai" ? (
            <div className="grid grid-cols-2 gap-3">
              <SelectField
                className="flex-col items-start w-full col-span-2 gap-1"
                inputClassName="h-9 px-2"
                label="Model"
                value={
                  provider === "openai"
                    ? openAIConfig.model
                    : googleConfig.model
                }
                options={providerModelFieldOptions}
                onChange={(event) => {
                  if (provider === "openai") {
                    setOpenAIConfig((prev) => ({
                      ...prev,
                      model: event.target.value,
                    }));
                  } else {
                    setGoogleConfig((prev) => ({
                      ...prev,
                      model: event.target.value,
                    }));
                  }
                }}
              />
              {provider === "google" ? (
                <>
                  <SelectField
                    className="flex-col items-start w-full gap-1"
                    inputClassName="h-9 px-2"
                    label="Aspect Ratio"
                    value={googleConfig.aspectRatio ?? ASPECT_RATIO_AUTO_VALUE}
                    options={GOOGLE_ASPECT_RATIO_FIELD_OPTIONS}
                    onChange={(event) => {
                      setGoogleConfig((prev) => ({
                        ...prev,
                        aspectRatio:
                          event.target.value === ASPECT_RATIO_AUTO_VALUE
                            ? null
                            : event.target.value,
                      }));
                    }}
                  />
                  {showImageSizeField && (
                    <SelectField
                      className="flex-col items-start w-full gap-1"
                      inputClassName="h-9 px-2"
                      label="Image Size"
                      value={googleConfig.imageSize ?? IMAGE_SIZE_AUTO_VALUE}
                      options={GOOGLE_IMAGE_SIZE_FIELD_OPTIONS}
                      onChange={(event) => {
                        setGoogleConfig((prev) => ({
                          ...prev,
                          imageSize:
                            event.target.value === IMAGE_SIZE_AUTO_VALUE
                              ? null
                              : event.target.value,
                        }));
                      }}
                    />
                  )}
                </>
              ) : (
                <SelectField
                  className="flex-col items-start w-full gap-1"
                  inputClassName="h-9 px-2"
                  label="Size"
                  value={openAIConfig.size ?? OPENAI_SIZE_AUTO_VALUE}
                  options={OPENAI_SIZE_FIELD_OPTIONS}
                  onChange={(event) => {
                    setOpenAIConfig((prev) => ({
                      ...prev,
                      size:
                        event.target.value === OPENAI_SIZE_AUTO_VALUE
                          ? null
                          : event.target.value,
                    }));
                  }}
                />
              )}
              <div className="flex flex-col col-span-2 gap-1">
                <label className="text-md font-medium text-gray-700">
                  Prompt
                </label>
                <InputTextarea
                  className="w-full px-2 border border-gray-300 rounded"
                  rows={4}
                  value={
                    provider === "openai"
                      ? openAIConfig.prompt
                      : googleConfig.prompt
                  }
                  onChange={(event) => {
                    if (provider === "openai") {
                      setOpenAIConfig((prev) => ({
                        ...prev,
                        prompt: event.target.value,
                      }));
                    } else {
                      setGoogleConfig((prev) => ({
                        ...prev,
                        prompt: event.target.value,
                      }));
                    }
                  }}
                />
              </div>
            </div>
          ) : provider === "vertex" ? (
            <div className="grid grid-cols-2 gap-3">
              <SelectField
                className="flex-col items-start w-full col-span-2 gap-1"
                inputClassName="h-9 px-2"
                label="Model"
                value={vertexConfig.model}
                options={googleModelFieldOptions}
                onChange={(event) => {
                  setVertexConfig((prev) => ({
                    ...prev,
                    model: event.target.value,
                  }));
                }}
              />
              <SelectField
                className="flex-col items-start w-full gap-1"
                inputClassName="h-9 px-2"
                label="Aspect Ratio"
                value={vertexConfig.aspectRatio ?? ASPECT_RATIO_AUTO_VALUE}
                options={GOOGLE_ASPECT_RATIO_FIELD_OPTIONS}
                onChange={(event) => {
                  setVertexConfig((prev) => ({
                    ...prev,
                    aspectRatio:
                      event.target.value === ASPECT_RATIO_AUTO_VALUE
                        ? null
                        : event.target.value,
                  }));
                }}
              />
              {showVertexImageSizeField ? (
                <SelectField
                  className="flex-col items-start w-full gap-1"
                  inputClassName="h-9 px-2"
                  label="Image Size"
                  value={vertexConfig.imageSize ?? IMAGE_SIZE_AUTO_VALUE}
                  options={GOOGLE_IMAGE_SIZE_FIELD_OPTIONS}
                  onChange={(event) => {
                    setVertexConfig((prev) => ({
                      ...prev,
                      imageSize:
                        event.target.value === IMAGE_SIZE_AUTO_VALUE
                          ? null
                          : event.target.value,
                    }));
                  }}
                />
              ) : (
                <div />
              )}
              <div className="flex flex-col col-span-2 gap-1">
                <label className="text-md font-medium text-gray-700">
                  Prompt
                </label>
                <InputTextarea
                  className="w-full px-2 border border-gray-300 rounded"
                  rows={4}
                  value={vertexConfig.prompt}
                  onChange={(event) => {
                    setVertexConfig((prev) => ({
                      ...prev,
                      prompt: event.target.value,
                    }));
                  }}
                />
              </div>
            </div>
          ) : provider === "cutout.pro" ? (
            <div className="grid grid-cols-2 gap-3">
              <SelectField
                className="flex-col items-start w-full gap-1"
                inputClassName="h-9 px-2"
                label="Crop"
                value={cutoutConfig.crop ? "true" : "false"}
                options={CUTOUT_CROP_FIELD_OPTIONS}
                onChange={(event) => {
                  setCutoutConfig((prev) => ({
                    ...prev,
                    crop: event.target.value === "true",
                  }));
                }}
              />
              <SelectField
                className="flex-col items-start w-full gap-1"
                inputClassName="h-9 px-2"
                label="Output Format"
                value={cutoutConfig.outputFormat}
                options={CUTOUT_OUTPUT_FORMAT_FIELD_OPTIONS}
                onChange={(event) => {
                  setCutoutConfig((prev) => ({
                    ...prev,
                    outputFormat: event.target
                      .value as CutoutConfigForm["outputFormat"],
                  }));
                }}
              />
            </div>
          ) : provider === "built_in_processing" ? (
            <div className="grid grid-cols-2 gap-3">
              <SelectField
                className="flex-col items-start w-full col-span-2 gap-1"
                inputClassName="h-9 px-2"
                label="Model"
                value={builtInProcessingConfig.model}
                options={googleModelFieldOptions}
                onChange={(event) => {
                  setBuiltInProcessingConfig((prev) => ({
                    ...prev,
                    model: event.target.value,
                  }));
                }}
              />
              <SelectField
                className="flex-col items-start w-full gap-1"
                inputClassName="h-9 px-2"
                label="Aspect Ratio"
                value={
                  builtInProcessingConfig.aspectRatio ?? ASPECT_RATIO_AUTO_VALUE
                }
                options={GOOGLE_ASPECT_RATIO_FIELD_OPTIONS}
                onChange={(event) => {
                  setBuiltInProcessingConfig((prev) => ({
                    ...prev,
                    aspectRatio:
                      event.target.value === ASPECT_RATIO_AUTO_VALUE
                        ? null
                        : event.target.value,
                  }));
                }}
              />
              {showBuiltInImageSizeField ? (
                <SelectField
                  className="flex-col items-start w-full gap-1"
                  inputClassName="h-9 px-2"
                  label="Image Size"
                  value={
                    builtInProcessingConfig.imageSize ?? IMAGE_SIZE_AUTO_VALUE
                  }
                  options={GOOGLE_IMAGE_SIZE_FIELD_OPTIONS}
                  onChange={(event) => {
                    setBuiltInProcessingConfig((prev) => ({
                      ...prev,
                      imageSize:
                        event.target.value === IMAGE_SIZE_AUTO_VALUE
                          ? null
                          : event.target.value,
                    }));
                  }}
                />
              ) : (
                <div />
              )}
            </div>
          ) : (
            <div className="text-gray-600 text-md">
              No configuration required.
            </div>
          )}
          <div className="grid grid-cols-2 gap-3 mt-3">
            <PostProcessingFields
              postProcessingType={postProcessingValues.postProcessingType}
              postProcessingConfig={postProcessingValues.postProcessingConfig}
              onChange={onChangePostProcessing}
            />
          </div>
          {showReferenceImageField && (
            <div className="mt-3">
              <label className="text-md font-medium text-gray-700">
                Reference Images{" "}
                <span className="font-normal text-gray-400">(optional, max 3)</span>
              </label>
              <div className="mt-1 flex flex-wrap gap-2">
                {[
                  ...referenceImageUrls,
                  ...(referenceImageUrls.length < 3 ? [""] : []),
                ].map((url, index) => (
                  <ClipArtInputField
                    key={
                      index < referenceImageUrls.length
                        ? `${index}-${url}`
                        : `new-ref-slot-${newRefSlotKey}`
                    }
                    className="max-h-35 min-h-35 w-35 flex-col items-start"
                    label=""
                    defaultValue={url ? storageApi.getURLFromPath(url) : undefined}
                    onChange={(ca: ClipArt | undefined) => {
                      if (!ca) {
                        if (index < referenceImageUrls.length) {
                          setReferenceImageUrls(
                            referenceImageUrls.filter((_, i) => i !== index),
                          );
                        }
                      } else {
                        if (index < referenceImageUrls.length) {
                          const updated = [...referenceImageUrls];
                          updated[index] = ca.path;
                          setReferenceImageUrls(updated);
                        } else {
                          setReferenceImageUrls([...referenceImageUrls, ca.path]);
                          setNewRefSlotKey((k) => k + 1);
                        }
                      }
                    }}
                  />
                ))}
              </div>
            </div>
          )}
        </Fieldset>
      </div>
    </Dialog>
  );
};

export {
  AIFilterActions,
  AIFilterConfig,
  AIFilterDetails,
  AIFilterName,
  AIFilterProviderCell,
  AIFilterTypeCell,
};
